// React Libraries and Hooks
import React, { useState, useEffect, useRef } from 'react';

// Material UI Components
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';

// Local Definitions from 'definitions' file
import {
  languages,
  lengths,
  temperatures,
  tones,
  teams,
  teamServiceTypeMapping,
  serviceTypes,
  batchServiceTypes,
  models,
  publications,
  empty,
  booleans
} from "../../definitions";

// Local Components
import CustomOptions from './CustomOptions';

// Import validation function
import sanitiseSettings from './settingsValidation';

const deliveryModeOptions = [
  { value: 'direct', label: 'Direct (sync)' },
  { value: 'queue', label: 'Queue (async)' },
  { value: 'stream', label: 'Stream (realtime)' },
];

const AdvancedOptions = ({
  serviceMode = "chat",
  input,
  setInput,
  skipLocalStorageHydration = false,
  linkHydrationOffer = null
}) => {
  // Initially check localStorage to see if the accordion should be expanded
  const localStorageKey = `scribe-${serviceMode}-options`;
  const savedSettings = JSON.parse(localStorage.getItem(localStorageKey) || '{}');

  // Determine if the accordion should be expanded by default either because it was previously expanded
  const isExpandedByDefault = savedSettings.expanded;

  // Set the initial state based on the above logic
  const [expanded, setExpanded] = useState(isExpandedByDefault ? 'settingsPanel' : false);

  // ensure hydration considered once
  const didHydrateRef = useRef(false);
  const didOfferPersistRef = useRef(false);

  useEffect(() => {
    // If caller wants to skip hydration (e.g., URL params already applied), bail once
    if (skipLocalStorageHydration || didHydrateRef.current) return;

    const raw = localStorage.getItem(localStorageKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        // One-time migration from legacy flags to deliveryMode
        if (!parsed.deliveryMode) {
          if (parsed?.options?.stream) parsed.deliveryMode = 'stream';
          else if (parsed?.useQueue) parsed.deliveryMode = 'queue';
        }

        const sanitised = sanitiseSettings(parsed);

        // fix small typo from older code: input.option -> input.options
        const updatedOptions = sanitised.options || input.options;
        const updatedSettings = sanitised.settings || input.settings;

        // write back the sanitised version
        localStorage.setItem(localStorageKey, JSON.stringify(sanitised));

        setInput(prevState => ({
          ...prevState,
          ...sanitised,
          options: { ...prevState.options, ...updatedOptions },
          settings: { ...prevState.settings, ...updatedSettings },
        }));
      } catch (error) {
        console.error('Error parsing or sanitising settings:', error);
      }
    }

    didHydrateRef.current = true;
    // eslint-disable-next-line
  }, [skipLocalStorageHydration]);

  // If we arrived via a link with params, offer to save as defaults (one-time)
  const [showPersistOffer, setShowPersistOffer] = useState(false);
  useEffect(() => {
    if (didOfferPersistRef.current) return;
    if (linkHydrationOffer) {
      setShowPersistOffer(true);
      didOfferPersistRef.current = true;
    }
  }, [linkHydrationOffer]);

  const persistLinkAsDefaults = (accept) => {
    setShowPersistOffer(false);
    if (!accept || !linkHydrationOffer) return;

    const nextLocal = JSON.parse(localStorage.getItem(localStorageKey) || '{}');

    // Build a compact object to save
    const sysMsg =
      input.options.messages.find(m => m.role === 'system')?.content || linkHydrationOffer.systemMessage || "";

    const mergeOptions = {
      ...(nextLocal.options || {}),
      model: input.options.model,
      temperature: input.options.temperature,
      max_length: input.options.max_length,
      ...(sysMsg ? { messages: [{ role: 'system', content: sysMsg }] } : {}),
      suggestionsEnabled: input.options.suggestionsEnabled,
    };

    const mergeSettings = {
      ...(nextLocal.settings || {}),
      language: input.settings.language,
      articleTone: input.settings.articleTone,
      writingStyle: input.settings.writingStyle,
      serviceType: input.settings.serviceType,
      team: input.settings.team,
    };

    const derivedMode =
      input.deliveryMode || (input.useQueue ? 'queue' : 'direct');

    const updated = {
      ...nextLocal,
      options: mergeOptions,
      settings: mergeSettings,
      deliveryMode: derivedMode,
      expanded: nextLocal.expanded ?? false
    };

    localStorage.setItem(localStorageKey, JSON.stringify(updated));
  };

  const handleChange = (field, value, nestedField = null) => {
    const newValue =
      value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value')
        ? value.value
        : value;

    setInput(prevInput => {
      const updatedInput = { ...prevInput };
      const updatedSettings = JSON.parse(localStorage.getItem(localStorageKey) || '{}');

      if (field === 'systemMessage') {
        const systemMessageIndex = updatedInput.options.messages.findIndex(msg => msg.role === 'system');

        if (newValue === '') {
          if (systemMessageIndex >= 0) {
            updatedInput.options.messages.splice(systemMessageIndex, 1);
          }
          // Remove the system message from localStorage
          if (updatedSettings.options && updatedSettings.options.messages) {
            updatedSettings.options.messages = updatedSettings.options.messages.filter(msg => msg.role !== 'system');
          }
        } else {
          if (systemMessageIndex >= 0) {
            updatedInput.options.messages[systemMessageIndex].content = newValue;
          } else {
            updatedInput.options.messages.unshift({ role: 'system', content: newValue });
          }
          updatedSettings.options = {
            ...updatedSettings.options,
            messages: [{ role: 'system', content: newValue }]
          };
        }
      } else if (nestedField) {
        updatedInput[nestedField][field] = newValue;
        updatedSettings[nestedField] = { ...updatedSettings[nestedField], [field]: newValue };
      } else {
        updatedInput[field] = newValue;
        updatedSettings[field] = newValue;
      }

      // keep serviceType in range for the selected team
      if (field === 'team') {
        const newFilteredServiceTypes = teamServiceTypeMapping[newValue] || serviceTypes.map(st => st.value);
        if (!newFilteredServiceTypes.includes(updatedInput.settings.serviceType)) {
          const newServiceTypeValue = newFilteredServiceTypes.length > 0 ? newFilteredServiceTypes[0] : '';
          updatedInput.settings.serviceType = newServiceTypeValue;
          updatedSettings.settings = { ...(updatedSettings.settings || {}), serviceType: newServiceTypeValue };
        }
      }

      localStorage.setItem(localStorageKey, JSON.stringify(updatedSettings));
      return updatedInput;
    });
  };

  const handleDeliveryModeChange = (modeValue) => {
    setInput(prev => {
      const next = { ...prev };
      const useQueue = modeValue === 'queue';

      next.deliveryMode = modeValue;
      next.useQueue = useQueue; // backward-compat if anyone still reads it

      const ls = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
      ls.deliveryMode = modeValue;
      ls.useQueue = useQueue;
      localStorage.setItem(localStorageKey, JSON.stringify(ls));

      return next;
    });
  };

  const filteredServiceTypes = (() => {
    // Select the appropriate service list
    const allServices = serviceMode === "batch-processor" ? batchServiceTypes : serviceTypes;

    // If a team is selected, filter services by that team's mapping
    if (input.settings.team && teamServiceTypeMapping[input.settings.team]) {
      const teamServices = teamServiceTypeMapping[input.settings.team];

      // Map team services to their full details (including image and description) from allServices
      return teamServices
        .map((serviceName) => allServices.find((service) => service.value === serviceName))
        .filter(Boolean) // Remove undefined values (services not found)
        .map(({ value, label, description, image }) => ({
          value,
          label,
          description: description || null,
          image: image || null,
        }));
    }

    // If no team is selected, return all available services
    return allServices.map(({ value, label, description, image }) => ({
      value,
      label,
      description: description || null,
      image: image || null,
    }));
  })();

  const teamOptions = [
    {
      type: 'autocomplete',
      label: 'Team',
      value: teams.find(option => option.value === input.settings.team) || null,
      onChange: (newValue) => handleChange('team', newValue ? newValue.value : '', 'settings'),
      options: teams,
      tooltipTitle: 'Selecting a team will tailor the available services.',
      disableClearable: true,
      fullWidth: true,
      sx: { mb: 2 },
      gridSize: { xs: 12 }
    },
  ];

  let serviceTypeOptions = [
    {
      type: 'richautocomplete',
      label: 'Service',
      value: filteredServiceTypes.find(option => option.value === input.settings.serviceType) || null,
      onChange: (newValue) => handleChange('serviceType', newValue ? newValue.value : '', 'settings'),
      options: filteredServiceTypes,
      tooltipTitle: 'Choose the service to customise your AI experience.',
      fullWidth: true,
      sx: { mb: 2 },
      gridSize: { xs: 12 }
    }
  ];

  if (input.settings.secondServiceType) {
    serviceTypeOptions.push({
      type: 'autocomplete',
      label: 'Service Type for optional second aggregated column',
      value: filteredServiceTypes.find(option => option.value === input.settings.secondServiceType) || null,
      onChange: (newValue) => handleChange('secondServiceType', newValue ? newValue.value : '', 'settings'),
      options: filteredServiceTypes,
      tooltipTitle: 'Select the secondary service.',
      fullWidth: true,
      sx: { mb: 2 },
      gridSize: { xs: 12 }
    });
  }

  if (input.settings.thirdServiceType) {
    serviceTypeOptions.push({
      type: 'autocomplete',
      label: 'Service Type for optional third aggregated column',
      value: filteredServiceTypes.find(option => option.value === input.settings.thirdServiceType) || null,
      onChange: (newValue) => handleChange('thirdServiceType', newValue ? newValue.value : '', 'settings'),
      options: filteredServiceTypes,
      tooltipTitle: 'Select the third service.',
      fullWidth: true,
      sx: { mb: 2 },
      gridSize: { xs: 12 }
    });
  }

  const currentDeliveryMode = (() => {
    if (input.deliveryMode) return input.deliveryMode;
    if (input.useQueue) return 'queue'; // legacy fallback
    const persisted = JSON.parse(localStorage.getItem(localStorageKey) || '{}')?.deliveryMode;
    return persisted || 'direct';
  })();

  let options = [
    {
      type: 'autocomplete',
      label: 'Language',
      value: languages.find(option => option.value === input.settings.language) || null,
      onChange: (newValue) => handleChange('language', newValue ? newValue.value : '', 'settings'),
      options: languages,
      tooltipTitle: 'Language used for inputs and outputs.'
    },
    {
      type: 'autocomplete',
      label: 'Model',
      value: models.find(option => option.value === input.options.model) || null,
      onChange: (newValue) => handleChange('model', newValue ? newValue.value : '', 'options'),
      options: models,
      tooltipTitle: 'Choose the model to use.'
    },
    {
      type: 'autocomplete',
      label: 'Temperature',
      value: temperatures.find(option => option.value === input.options.temperature) || null,
      onChange: (newValue) => handleChange('temperature', newValue ? newValue.value : '', 'options'),
      options: temperatures,
      tooltipTitle: 'Higher = more creative, lower = more deterministic.'
    },
    {
      type: 'autocomplete',
      label: 'Length',
      value: lengths.find(option => option.value === input.options.max_length) || null,
      onChange: (newValue) => handleChange('max_length', newValue ? newValue.value : '', 'options'),
      options: lengths,
      tooltipTitle: 'Control the verbosity of responses.'
    },
    {
      type: 'autocomplete',
      label: 'Tone',
      value: tones.find(option => option.value === input.settings.articleTone) || null,
      onChange: (newValue) => {
        handleChange('articleTone', newValue ? newValue.value : '', 'settings');
        handleChange('writingStyle', empty.value, 'settings');
      },
      options: tones,
      tooltipTitle: 'Set the mood and style of content.',
      disableClearable: false
    },
    {
      type: 'autocomplete',
      label: 'Publication Tone',
      value: publications.find(option => option.value === input.settings.writingStyle) || null,
      onChange: (newValue) => {
        handleChange('writingStyle', newValue ? newValue.value : '', 'settings');
        handleChange('articleTone', empty.value, 'settings');
      },
      options: publications,
      tooltipTitle: 'Align with a publication voice.',
      disableClearable: false
    },
    {
      type: 'text',
      label: 'Custom Service Message Override',
      value: input.options.messages.find(msg => msg.role === 'system')?.content || '',
      onChange: (newValue) => handleChange('systemMessage', newValue),
      options: [],
      tooltipTitle: 'Override the default system/service message for fine control.'
    }
  ];

  // Insert Delivery Mode selector just before the final "Custom Service Message" text field
  options.splice(options.length - 1, 0, {
    type: 'autocomplete',
    label: 'Delivery Mode',
    value: deliveryModeOptions.find(o => o.value === currentDeliveryMode) || deliveryModeOptions[0],
    onChange: (newValue) => handleDeliveryModeChange(newValue ? newValue.value : 'direct'),
    options: deliveryModeOptions,
    tooltipTitle: 'Choose how to send the request: direct (sync), queue (async), or stream (realtime).'
  });

  if (input.options.suggestionsEnabled !== undefined) {
    options.push({
      type: 'switch',
      label: 'Enable Suggested Prompt',
      value: input.options.suggestionsEnabled,
      onChange: (newValue) => handleChange('suggestionsEnabled', newValue, 'options'),
      tooltipTitle: 'Enable or disable suggested prompts.',
      fullWidth: true,
      gridSize: { xs: 12 },
    });
  }

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
    const updatedSettings = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
    updatedSettings.expanded = isExpanded;
    localStorage.setItem(localStorageKey, JSON.stringify(updatedSettings));
  };

  return (
    <>
      {/* One-time prompt when arriving via shared link */}
      {showPersistOffer && (
        <Stack sx={{ mb: 2 }} spacing={1}>
          <Alert
            severity="info"
            action={
              <>
                <Button color="inherit" size="small" onClick={() => persistLinkAsDefaults(true)}>
                  Yes
                </Button>
                <Button color="inherit" size="small" onClick={() => persistLinkAsDefaults(false)}>
                  No
                </Button>
              </>
            }
          >
            You opened a shared setup. Do you want to save these settings as your defaults?
          </Alert>
        </Stack>
      )}

      {input.settings.team && <CustomOptions options={teamOptions} />}
      <CustomOptions options={serviceTypeOptions} />
      <Accordion expanded={expanded === 'settingsPanel'} onChange={handleAccordionChange('settingsPanel')}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
        >
          <Typography>Advanced Options</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CustomOptions options={options} />
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default AdvancedOptions;
